// فهرستِ کشورها و شهرها (مایگریشن 0050) — فقط خوندنی.
//
// این‌جا برخلافِ بقیه‌ی repoها هیچ چیزی از آنِ کاربر نیست: یه جدولِ مرجعه که
// همه یه چیز رو ازش می‌بینن. برای همین هم دیتای دمو از demoStore نمی‌آد؛
// چند نمونه‌ی ثابتِ پایینِ همین فایل کافیه تا فرمِ دمو کار کنه.
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { CITY_PAGE, rankCities } from '../lib/cityRank';
import { pickHomeCity } from '../lib/nearest';
import type { City, Country } from '../domain/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * بدون بک‌اند (دمو یا کلیدِ تنظیم‌نشده) فرم نباید خالی بمونه.
 * تابعه نه ثابت: isSupabaseConfigured به initCore() نیاز داره و این فایل
 * ممکنه زودتر از اون import بشه — نتیجه‌ش هر بار موقعِ صدا زدن حساب می‌شه.
 */
const offline = () => !isSupabaseConfigured() || isDemoMode;

const DEMO_COUNTRIES: Country[] = [
  { code: 'CA', name: 'Canada', emoji: '🇨🇦' },
  { code: 'DE', name: 'Germany', emoji: '🇩🇪' },
  { code: 'IR', name: 'Iran', emoji: '🇮🇷' },
  { code: 'US', name: 'United States', emoji: '🇺🇸' },
];

const DEMO_CITIES: City[] = [
  { id: 1, name: 'Ottawa', countryCode: 'CA', region: 'Ontario' },
  { id: 2, name: 'Toronto', countryCode: 'CA', region: 'Ontario' },
  { id: 3, name: 'Gatineau', countryCode: 'CA', region: 'Quebec' },
  { id: 4, name: 'Berlin', countryCode: 'DE', region: 'Berlin' },
  { id: 5, name: 'Tehran', countryCode: 'IR', region: 'Tehran' },
  { id: 6, name: 'New York City', countryCode: 'US', region: 'New York' },
];

/**
 * چیزی که کاربر تایپ کرده، تمیزشده برای `ilike`.
 *
 * `%` و `_` توی الگو معنی دارن و `,` رشته‌ی کوئریِ PostgREST رو می‌شکنه —
 * پس نه فیلتر می‌شن که نتیجه‌ی عجیب بده، حذف می‌شن.
 */
const clean = (q: string) => q.trim().replace(/[%_,()*\\]/g, ' ').replace(/\s+/g, ' ');

const mapCity = (c: any): City => ({
  id: Number(c.id), name: c.name, countryCode: c.country_code, region: c.region ?? null,
});

/** مختصاتِ چند شهرِ دمو، فقط تا دکمه‌ی «از موقعیتم پیدا کن» بدونِ بک‌اند هم جواب بده. */
const DEMO_COORDS: Record<number, [number, number]> = {
  1: [45.4215, -75.6972],   // Ottawa
  2: [43.6532, -79.3832],   // Toronto
  3: [45.4765, -75.7013],   // Gatineau
  4: [52.52, 13.405],       // Berlin
  5: [35.6892, 51.389],     // Tehran
  6: [40.7128, -74.006],    // New York City
};

/** کادرِ تنگ اول، بعد گشاد. درجه — حدودِ ۱۱۰ و ۴۴۰ کیلومتر. */
const NEAR_BOXES = [1, 4];

export const geoRepo = {
  /**
   * همه‌ی کشورها، یک‌جا.
   *
   * ۲۵۰ ردیفه — چند ده کیلوبایت — پس جستجوش سمتِ خودِ گوشی انجام می‌شه و
   * تایپ کردن هیچ رفت‌وبرگشتی به سرور نداره. شهرها داستانشون فرق داره.
   */
  async list(): Promise<Country[]> {
    if (offline()) return DEMO_COUNTRIES;
    const { data, error } = await supabase
      .from('countries').select('code, name, emoji').order('name');
    if (error) throw error;
    return (data ?? []).map((c: any) => ({ code: c.code, name: c.name, emoji: c.emoji }));
  },

  /**
   * شهرهای یه کشور.
   *
   * ترتیب دو مرحله‌ست:
   *
   *   ۱. **جمعیت، نزولی** (و بعد الفبا برای هم‌جمعیت‌ها). بدونِ عبارتِ جستجو
   *      همینه و بس — پرجمعیت‌ترین شهرهای اون کشور همون‌هایی‌ان که احتمالِ
   *      انتخاب شدنشون بیشتره، نه اولین‌های الفبا.
   *   ۲. موقعِ جستجو، **کیفیتِ تطبیق** روی اون می‌شینه (`rankCities`): اول
   *      اسمِ دقیقاً برابر، بعد اونی که با عبارت شروع می‌شه، بعد اونی که فقط
   *      شاملشه — و داخلِ هر گروه، همون ترتیبِ جمعیت.
   *
   * برای همین موقعِ جستجو ۲۰۰ ردیف گرفته می‌شه و ۶۰ تای اولِ **بعد از**
   * رتبه‌بندی برمی‌گرده: اگه رتبه‌بندی روی چیزی که سرور به ۶۰ بریده انجام شه،
   * شهرِ کوچیکی که اسمش دقیقاً همونه اصلاً به دستمون نمی‌رسه. ۱۵۳ هزار شهر
   * هیچ‌وقت کامل دانلود نمی‌شه.
   */
  async cities(countryCode: string, search = ''): Promise<City[]> {
    const q = clean(search);
    if (offline()) {
      return rankCities(DEMO_CITIES.filter((c) => c.countryCode === countryCode), q);
    }
    let req = supabase.from('cities')
      .select('id, name, country_code, region')
      .eq('country_code', countryCode);
    if (q) req = req.ilike('name', `%${q}%`);
    const { data, error } = await req
      .order('population', { ascending: false }).order('name')
      .limit(q ? 200 : CITY_PAGE);
    if (error) throw error;
    return rankCities((data ?? []).map(mapCity), q);
  },

  /**
   * نزدیک‌ترین شهرِ فهرست به این مختصات — برای دکمه‌ی «از موقعیتم پیدا کن»
   * روی فرمِ ساختِ نمایه.
   *
   * چرا با کادرِ مختصات و نه یک RPC روی دیتابیس: تابعِ SQL یعنی مایگریشنِ
   * تازه، و مایگریشن یعنی چیزی که باید **پیش از** انتشارِ کد روی دیتابیسِ
   * ابری اجرا شود وگرنه کد روی چیزی تکیه می‌کند که آن‌جا نیست (همان
   * «schema drift» که publish-update.mjs جلویش را می‌گیرد). این‌جا همان کار
   * با ستون‌هایی که از مایگریشن 0050 موجودند انجام می‌شود.
   *
   * دو مرحله‌ای است چون کادرِ تنگ معمولاً جواب می‌دهد و ارزان است؛ کادرِ
   * گشاد فقط برای جایی که واقعاً هیچ شهری نزدیک نیست (وسطِ دریا، بیابان).
   */
  async nearestCity(lat: number, lng: number): Promise<{ city: City; country: Country } | null> {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const countries = await this.list();
    const withCountry = (c: City) => {
      const country = countries.find((x) => x.code === c.countryCode);
      return country ? { city: c, country } : null;
    };

    if (offline()) {
      const picked = pickHomeCity(lat, lng, DEMO_CITIES.map((c) => ({
        ...c, lat: DEMO_COORDS[c.id]?.[0] ?? null, lng: DEMO_COORDS[c.id]?.[1] ?? null,
        population: null,
      })));
      return picked ? withCountry(picked) : null;
    }

    for (const deg of NEAR_BOXES) {
      // یک درجه طولِ جغرافیایی هرچه از استوا دورتر، کوتاه‌تر — بدونِ این
      // تقسیم، کادر در عرض‌های بالا (مثلاً کانادا) بیش از حد باریک می‌شود.
      const dLng = deg / Math.max(0.2, Math.cos((lat * Math.PI) / 180));
      // جمعیت هم می‌آد، نه فقط برای ترتیب: `pickHomeCity` باهاش تصمیم می‌گیره
      // که شهرِ بزرگ‌ترِ کنارِ دست جای نزدیک‌ترین رو بگیره یا نه.
      const { data, error } = await supabase.from('cities')
        .select('id, name, country_code, region, lat, lng, population')
        .gte('lat', lat - deg).lte('lat', lat + deg)
        .gte('lng', lng - dLng).lte('lng', lng + dLng)
        .order('population', { ascending: false })
        .limit(300);
      if (error) throw error;
      const rows = (data ?? []).map((c: any) => ({
        ...mapCity(c),
        lat: c.lat as number | null,
        lng: c.lng as number | null,
        population: c.population as number | null,
      }));
      const picked = pickHomeCity(lat, lng, rows);
      if (picked) return withCountry(picked);
    }
    return null;
  },

  /**
   * کشوری با این اسم هست یا نه.
   *
   * پروفایل اسم رو نگه می‌داره نه کد، و اسم‌های قدیمی از زمانی مونده‌ن که این
   * فیلد متنِ آزاد بود. صفحه‌ی نمایه با همین چک می‌فهمه چیزی که ذخیره شده هنوز
   * انتخابِ معتبریه یا باید دوباره انتخاب شه.
   */
  async findCountry(name: string): Promise<Country | null> {
    const n = name.trim();
    if (!n) return null;
    if (offline()) {
      return DEMO_COUNTRIES.find((c) => c.name.toLowerCase() === n.toLowerCase()) ?? null;
    }
    const { data, error } = await supabase
      .from('countries').select('code, name, emoji').ilike('name', clean(n)).limit(1);
    if (error) throw error;
    const row = (data ?? [])[0];
    return row ? { code: row.code, name: row.name, emoji: row.emoji } : null;
  },

  /** همون چک، برای شهر — و فقط داخلِ کشورِ خودش. */
  async hasCity(countryCode: string, name: string): Promise<boolean> {
    const n = clean(name);
    if (!n) return false;
    if (offline()) {
      return DEMO_CITIES.some((c) => c.countryCode === countryCode
        && c.name.toLowerCase() === n.toLowerCase());
    }
    const { count, error } = await supabase
      .from('cities').select('id', { count: 'exact', head: true })
      .eq('country_code', countryCode).ilike('name', n);
    if (error) throw error;
    return (count ?? 0) > 0;
  },
};
